/**
 * @description 创建一个确保函数在同一时间内仅运行一次的单例版本。
 * 如果函数正在执行中，则后续的调用将被忽略，并立即返回 undefined。
 * 这对于防止用户重复点击提交或发起重复请求非常有用。
 *
 * @template T - 一个数组类型，代表原函数的参数类型列表。
 * @template R - 原函数的返回值类型。
 *
 * @param fn - 需要被包装成单例模式的原始函数（可以是同步或异步函数）。
 * @returns - 一个新的函数，具有与原函数相同的签名，但保证了单例执行。
 *            如果调用被忽略，异步函数会返回一个解析为 undefined 的 Promise，同步函数直接返回 undefined。
 */
export function singleRun<T extends any[], R>(
    fn: ( ...args: T ) => R
): ( ...args: T ) => R extends Promise<any> ? Promise<Awaited<R> | undefined> : R | undefined {

    // 1. 状态锁：利用闭包创建一个私有的 `isRunning` 变量作为锁。
    let isRunning = false;

    // 2. 返回新的包装函数，它将代替原函数被调用。
    // 使用 function 关键字以正确处理 `this` 上下文。
    return function( this: any, ...args: T ) {

        // 3. 检查锁：如果函数已在运行，则给出警告并直接返回，实现“忽略”效果。
        if ( isRunning ) {

            console.warn( `[singleRun] Function '${fn.name || 'anonymous'}' is already running. This call has been ignored.` );
            // 对于异步函数，返回一个 resolved Promise 是良好实践，避免调用方出现 .then of undefined 错误
            if ( fn.constructor.name === 'AsyncFunction' ) {

                return Promise.resolve( undefined ) as any;
            
            }
            return undefined as any;
        
        }

        // 4. 加锁：准备执行函数，立即上锁。
        isRunning = true;

        try {

            // 5. 执行原函数：使用 Reflect.apply 来安全地调用原函数，并传入正确的 `this` 上下文和参数。
            const result = Reflect.apply( fn, this, args );

            // 6. 处理异步情况：如果返回值是 Promise，则需要等待其完成后再解锁。
            if ( result instanceof Promise ) {

                return result.finally(() => {

                    isRunning = false; // 无论成功或失败，都在 finally 中解锁
                
                }) as any;
            
            }

            // 7. 处理同步情况：如果是同步函数，直接在这里解锁并返回结果。
            isRunning = false;
            return result as any;

        } catch ( error ) {

            // 8. 异常处理：如果在执行同步函数时发生错误，也要确保解锁。
            isRunning = false;
            throw error; // 重新抛出错误，保持原函数的行为
        
        }
    
    };

}
